import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ExpenseFormNew from './ExpenseFormNew';
import { useAuth } from '../../contexts/AuthContext';
import { ExpenseService } from '../../services/expense';
import { ExpenseServiceWrapper } from '../../services/expenseWrapper';
import { StorageService } from '../../services/storage';
import { GraphQLExpenseService } from '../../services/graphqlExpense';
import { canonicalizeEmail, findByIdentityWithManager } from '../../services/identity';
import { CognitoAdminService } from '../../services/cognitoAdmin';
import { emailExpenseSubmitted, emailExpenseDeleted } from '../../services/emailNotification';
import { notifyExpenseSubmitted, notifyExpenseDeleted } from '../../services/inAppNotification';
import { getFinanceAdminEmails } from '../../services/userLoader';
import { 
  Expense, 
  CreateExpenseInput, 
  UpdateExpenseInput, 
  Receipt,
  ExpenseStatus 
} from '../../types';

interface ExpenseManagerProps {
  expenseId?: string; // For editing existing expense
  mode?: 'create' | 'edit' | 'view';
  onSave?: (expense: Expense) => void;
  onCancel?: () => void;
}

const ExpenseManager: React.FC<ExpenseManagerProps> = ({
  expenseId: propExpenseId,
  mode = 'create',
  onSave,
  onCancel
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: urlExpenseId } = useParams<{ id: string }>();
  
  // Use expenseId from URL params if available, otherwise use prop
  const expenseId = urlExpenseId || propExpenseId;
  
  const [expense, setExpense] = useState<Expense | undefined>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Load existing expense if editing
  useEffect(() => {
    if (expenseId && mode !== 'create') {
      loadExpense();
    }
  }, [expenseId, mode]);

  const loadExpense = async () => {
    if (!expenseId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('[ExpenseManager] Loading expense:', expenseId);
      const loadedExpense = await GraphQLExpenseService.getExpense(expenseId);
      
      if (!loadedExpense) {
        throw new Error('Expense not found');
      }
      
      console.log('[ExpenseManager] Loaded expense:', loadedExpense);
      setExpense(loadedExpense);
    } catch (err) {
      console.error('[ExpenseManager] Failed to load expense:', err);
      setError(err instanceof Error ? err.message : 'Failed to load expense');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (data: CreateExpenseInput | UpdateExpenseInput, receiptFiles?: File[]) => {
    console.log('[ExpenseManager] ========== FORM SUBMISSION STARTED ==========');
    console.log('[ExpenseManager] Mode:', mode);
    console.log('[ExpenseManager] Form data:', data);
    console.log('[ExpenseManager] Receipt files:', receiptFiles?.length || 0);
    console.log('[ExpenseManager] User:', user);
    
    setSubmitting(true);
    setError(null);
    setValidationErrors([]);

    try {
      let savedExpense: Expense;

      if (mode === 'create') {
        console.log('[ExpenseManager] Creating new expense...');
        // Create new expense and submit for approval
        // Use email as submitterId for consistency with manager dashboard
        // Backend validation now accepts both UUID and email
        const draftExpense = await ExpenseServiceWrapper.createDraft(
          data as CreateExpenseInput,
          // Canonicalised to the current domain, so a session on the old-domain
          // Cognito account still files the row where new rows belong.
          canonicalizeEmail(user?.email) || user?.id,
          user?.name, // Pass user's name from Cognito
          user?.department // Pass user's actual department
        );
        console.log('[ExpenseManager] Draft expense created:', draftExpense);
        console.log('[ExpenseManager] Draft submitterId:', draftExpense.submitterId, 'user.email:', user?.email, 'department:', draftExpense.department);
        
        // Upload receipts if any
        if (receiptFiles && receiptFiles.length > 0) {
          console.log('[ExpenseManager] Uploading', receiptFiles.length, 'receipts for expense:', draftExpense.id);
          for (const file of receiptFiles) {
            try {
              // Upload to S3
              console.log('[ExpenseManager] Uploading file to S3:', file.name);
              const receipt = await StorageService.uploadReceipt(draftExpense.id, file);
              console.log('[ExpenseManager] Uploaded receipt to S3:', file.name, 'URL:', receipt.fileUrl);
              
              // Save receipt record to database
              console.log('[ExpenseManager] Saving receipt to database...');
              const savedReceipt = await GraphQLExpenseService.createReceipt({
                expenseId: draftExpense.id,
                fileName: receipt.fileName,
                fileUrl: receipt.fileUrl,
                fileKey: receipt.fileKey,
                fileType: receipt.fileType,
                fileSize: receipt.fileSize
              });
              console.log('[ExpenseManager] Saved receipt record to database:', file.name, 'ID:', savedReceipt?.id);
            } catch (uploadError: any) {
              console.error('[ExpenseManager] Failed to upload/save receipt:', file.name);
              console.error('[ExpenseManager] Error:', uploadError);
              if (uploadError.errors) {
                console.error('[ExpenseManager] GraphQL errors:', uploadError.errors);
              }
              // Continue with other files even if one fails
            }
          }
        }
        
        // Validate before submission
        console.log('[ExpenseManager] Validating expense before submission...');
        const validation = ExpenseServiceWrapper.validateForSubmission(draftExpense);
        console.log('[ExpenseManager] Validation result:', validation);
        if (!validation.isValid) {
          console.error('[ExpenseManager] Validation failed:', validation.errors);
          setValidationErrors(validation.errors);
          setSubmitting(false);
          return;
        }
        console.log('[ExpenseManager] Validation passed!');

        // Get manager email - if not in user context, fetch from Cognito Admin API
        let managerEmail = user?.managerEmail;
        if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
          try {
            console.log('[ExpenseManager] Fetching manager email from Cognito for:', user.email);
            const response = await CognitoAdminService.listUsers();
            const cognitoUser = findByIdentityWithManager(response.users, user?.email);
            if (cognitoUser?.managerEmail) {
              managerEmail = cognitoUser.managerEmail;
              console.log('[ExpenseManager] Found manager email:', managerEmail);
            }
          } catch (err) {
            console.warn('[ExpenseManager] Could not fetch manager email from Cognito:', err);
          }
        }

        // Submit for approval - pass user info for workflow routing
        console.log('[ExpenseManager] Submitting expense for approval...');
        console.log('[ExpenseManager] Manager email:', managerEmail);
        console.log('[ExpenseManager] User role:', user?.role);
        savedExpense = await ExpenseServiceWrapper.submitForApproval(draftExpense.id, {
          role: user?.role,
          managerEmail: managerEmail
        });
        console.log('[ExpenseManager] Expense submitted! New status:', savedExpense.status);
        
        // Send notifications based on the new status
        const employeeName = user?.name || 'Employee';
        console.log('[ExpenseManager] Sending notifications...');
        
        if (savedExpense.status === ExpenseStatus.SUBMITTED && managerEmail) {
          // Normal flow: Notify manager
          console.log('[ExpenseManager] Notifying manager:', managerEmail);
          await notifyExpenseSubmitted(managerEmail, employeeName, data.title || '', data.amount || 0, draftExpense.id);
          await emailExpenseSubmitted(managerEmail, employeeName, data.title || '', data.amount || 0, draftExpense.id);
        } else if (savedExpense.status === ExpenseStatus.MANAGER_APPROVED) {
          // Bypassed manager approval: Notify Finance Admin
          console.log('[ExpenseManager] Manager approval bypassed, notifying Finance Admin');
          try {
            const financeAdminEmails = await getFinanceAdminEmails();
            console.log('[ExpenseManager] Finance Admin emails:', financeAdminEmails);
            for (const adminEmail of financeAdminEmails) {
              console.log('[ExpenseManager] Sending notification to:', adminEmail);
              await notifyExpenseSubmitted(adminEmail, employeeName, data.title || '', data.amount || 0, draftExpense.id);
              await emailExpenseSubmitted(adminEmail, employeeName, data.title || '', data.amount || 0, draftExpense.id);
            }
            console.log('[ExpenseManager] Finance Admin notifications sent successfully');
          } catch (notifyErr) {
            console.error('[ExpenseManager] Could not notify finance admins:', notifyErr);
          }
        }
      } else if (expense) {
        // Update existing expense
        const updatedExpense = await ExpenseServiceWrapper.updateDraft(expense.id, data as UpdateExpenseInput);
        
        // Upload receipts if any
        if (receiptFiles && receiptFiles.length > 0) {
          for (const file of receiptFiles) {
            try {
              // Upload to S3
              const receipt = await StorageService.uploadReceipt(updatedExpense.id, file);
              
              // Save receipt record to database
              await GraphQLExpenseService.createReceipt({
                expenseId: updatedExpense.id,
                fileName: receipt.fileName,
                fileUrl: receipt.fileUrl,
                fileKey: receipt.fileKey,
                fileType: receipt.fileType,
                fileSize: receipt.fileSize
              });
            } catch (uploadError) {
              console.error('[ExpenseManager] Failed to upload receipt:', file.name, uploadError);
            }
          }
        }
        
        // Validate before submission
        const validation = ExpenseServiceWrapper.validateForSubmission(updatedExpense);
        if (!validation.isValid) {
          setValidationErrors(validation.errors);
          setSubmitting(false);
          return;
        }

        // Get manager email - if not in user context, fetch from Cognito Admin API
        let managerEmailForUpdate = user?.managerEmail;
        if (!managerEmailForUpdate && user?.email && CognitoAdminService.isConfigured()) {
          try {
            console.log('[ExpenseManager] Fetching manager email from Cognito for:', user.email);
            const response = await CognitoAdminService.listUsers();
            const cognitoUser = findByIdentityWithManager(response.users, user?.email);
            if (cognitoUser?.managerEmail) {
              managerEmailForUpdate = cognitoUser.managerEmail;
              console.log('[ExpenseManager] Found manager email:', managerEmailForUpdate);
            }
          } catch (err) {
            console.warn('[ExpenseManager] Could not fetch manager email from Cognito:', err);
          }
        }

        // Submit for approval - pass user info for workflow routing
        console.log('[ExpenseManager] Submitting updated expense for approval...');
        console.log('[ExpenseManager] Manager email:', managerEmailForUpdate);
        console.log('[ExpenseManager] User role:', user?.role);
        savedExpense = await ExpenseServiceWrapper.submitForApproval(updatedExpense.id, {
          role: user?.role,
          managerEmail: managerEmailForUpdate
        });
        console.log('[ExpenseManager] Updated expense submitted! New status:', savedExpense.status);

        // Send notifications based on the new status
        const empName = user?.name || 'Employee';
        console.log('[ExpenseManager] Sending notifications for updated expense...');
        
        if (savedExpense.status === ExpenseStatus.SUBMITTED && managerEmailForUpdate) {
          // Normal flow: Notify manager
          console.log('[ExpenseManager] Notifying manager:', managerEmailForUpdate);
          await notifyExpenseSubmitted(managerEmailForUpdate, empName, updatedExpense.title, updatedExpense.amount, updatedExpense.id);
          await emailExpenseSubmitted(managerEmailForUpdate, empName, updatedExpense.title, updatedExpense.amount, updatedExpense.id);
        } else if (savedExpense.status === ExpenseStatus.MANAGER_APPROVED) {
          // Bypassed manager approval: Notify Finance Admin
          console.log('[ExpenseManager] Manager approval bypassed for updated expense, notifying Finance Admin');
          try {
            const financeAdminEmails = await getFinanceAdminEmails();
            console.log('[ExpenseManager] Finance Admin emails:', financeAdminEmails);
            for (const adminEmail of financeAdminEmails) {
              console.log('[ExpenseManager] Sending notification to:', adminEmail);
              await notifyExpenseSubmitted(adminEmail, empName, updatedExpense.title, updatedExpense.amount, updatedExpense.id);
              await emailExpenseSubmitted(adminEmail, empName, updatedExpense.title, updatedExpense.amount, updatedExpense.id);
            }
            console.log('[ExpenseManager] Finance Admin notifications sent successfully');
          } catch (notifyErr) {
            console.error('[ExpenseManager] Could not notify finance admins:', notifyErr);
          }
        }
      } else {
        throw new Error('No expense to update');
      }

      setExpense(savedExpense);
      onSave?.(savedExpense);

      console.log('[ExpenseManager] ========== SUBMISSION SUCCESSFUL ==========');
      console.log('[ExpenseManager] Final expense:', savedExpense);
      
      // Show success message and navigate
      alert('Expense submitted successfully!');
      navigate('/expenses/my');

    } catch (err) {
      console.error('[ExpenseManager] ========== SUBMISSION FAILED ==========');
      console.error('[ExpenseManager] Error:', err);
      console.error('[ExpenseManager] Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      setError(err instanceof Error ? err.message : 'Failed to submit expense');
    } finally {
      setSubmitting(false);
      console.log('[ExpenseManager] Submission process completed. Submitting:', false);
    }
  };

  const handleSaveDraft = async (data: CreateExpenseInput | UpdateExpenseInput) => {
    setSaving(true);
    setError(null);

    try {
      let savedExpense: Expense;

      if (mode === 'create') {
        console.log('[ExpenseManager] Saving draft with user.email:', user?.email, 'user.id:', user?.id);
        // Use email as submitterId for consistency with manager dashboard filtering
        savedExpense = await ExpenseServiceWrapper.createDraft(
          data as CreateExpenseInput,
          canonicalizeEmail(user?.email) || user?.id,
          user?.name,
          user?.department // Pass user's actual department
        );
        console.log('[ExpenseManager] Draft saved with submitterId:', savedExpense.submitterId, 'department:', savedExpense.department);
      } else if (expense) {
        savedExpense = await ExpenseServiceWrapper.updateDraft(expense.id, data as UpdateExpenseInput);
      } else {
        throw new Error('No expense to update');
      }

      setExpense(savedExpense);
      onSave?.(savedExpense);

      // Show success message
      alert('Draft saved successfully!');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleReceiptUpload = async (file: File): Promise<Receipt> => {
    if (!expense) {
      throw new Error('No expense selected for receipt upload');
    }

    try {
      const receipt = await StorageService.uploadReceipt(expense.id, file);
      
      // Update expense with new receipt
      setExpense(prev => prev ? {
        ...prev,
        receipts: [...prev.receipts, receipt]
      } : prev);

      return receipt;
    } catch (error) {
      throw new Error(
        error instanceof Error 
          ? `Receipt upload failed: ${error.message}` 
          : 'Receipt upload failed'
      );
    }
  };

  const handleReceiptDelete = async (receiptId: string) => {
    if (!expense) return;

    try {
      const receipt = expense.receipts.find(r => r.id === receiptId);
      if (receipt) {
        await StorageService.deleteReceipt(receipt);
        
        // Update expense by removing the receipt
        setExpense(prev => prev ? {
          ...prev,
          receipts: prev.receipts.filter(r => r.id !== receiptId)
        } : prev);
      }
    } catch (error) {
      alert('Failed to delete receipt. Please try again.');
    }
  };

  const handleDeleteExpense = async () => {
    if (!expense || !ExpenseServiceWrapper.canDelete(expense)) {
      return;
    }

    const confirmMsg = expense.status === ExpenseStatus.SUBMITTED
      ? 'This expense has been submitted. Are you sure you want to delete it? Your manager will be notified.'
      : 'Are you sure you want to delete this expense? This action cannot be undone.';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const wasSubmitted = expense.status === ExpenseStatus.SUBMITTED;
      await ExpenseServiceWrapper.deleteDraft(expense.id);

      // Notify manager if the expense was in Submitted status
      if (wasSubmitted) {
        let managerEmail = user?.managerEmail;
        if (!managerEmail && user?.email && CognitoAdminService.isConfigured()) {
          try {
            const response = await CognitoAdminService.listUsers();
            const cognitoUser = findByIdentityWithManager(response.users, user?.email);
            if (cognitoUser?.managerEmail) {
              managerEmail = cognitoUser.managerEmail;
            }
          } catch (err) {
            console.warn('[ExpenseManager] Could not fetch manager email:', err);
          }
        }
        if (managerEmail) {
          const employeeName = user?.name || 'Employee';
          try {
            await notifyExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
            await emailExpenseDeleted(managerEmail, employeeName, expense.title, expense.amount);
            console.log('[ExpenseManager] Manager notified about expense deletion');
          } catch (notifyErr) {
            console.warn('[ExpenseManager] Could not notify manager about deletion:', notifyErr);
          }
        }
      }

      alert('Expense deleted successfully!');
      navigate('/expenses/my');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete expense');
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/expenses/my');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="text-lg text-gray-600">Loading expense...</div>
      </div>
    );
  }

  const canEdit = !expense || ExpenseServiceWrapper.canEdit(expense);
  const canDelete = expense && ExpenseServiceWrapper.canDelete(expense);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="text-red-800">
              <strong>Error:</strong> {error}
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="text-yellow-800">
            <strong>Please fix the following issues before submitting:</strong>
            <ul className="mt-2 list-disc list-inside space-y-1">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Expense Form */}
      <ExpenseFormNew
        expense={expense}
        onSubmit={handleFormSubmit}
        onSaveDraft={canEdit ? handleSaveDraft : undefined}
        onCancel={handleCancel}
        isLoading={submitting || saving}
        mode={mode === 'view' ? 'edit' : mode}
      />

      {/* Action Summary */}
      {expense && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Available Actions</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {ExpenseServiceWrapper.canEdit(expense) && (
              <div>✏️ You can edit this expense and save changes as a draft</div>
            )}
            {ExpenseServiceWrapper.canSubmit(expense) && (
              <div>📤 You can submit this expense for approval</div>
            )}
            {ExpenseServiceWrapper.canDelete(expense) && (
              <div>🗑️ You can delete this expense</div>
            )}
            {!ExpenseServiceWrapper.canEdit(expense) && (
              <div>🔒 This expense cannot be edited because it has been submitted for approval</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseManager;
